import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { ChatClient, ChatThreadClient, ChatMessage, ChatMessageReceivedEvent } from '@azure/communication-chat';
import { AzureCommunicationTokenCredential, CommunicationUserIdentifier } from '@azure/communication-common';
import './custom-chat.scss';
import { Button, Chat, ChatItemProps, CloseIcon, Flex, Input, SendIcon, Skeleton } from '@fluentui/react-northstar';

interface CustomChatProps {
    token: string;
    userId: string;
    displayName: string;
    threadId: string;
    endpointUrl: string;
    onClose: () => void;
}

const CustomChat: React.FC<CustomChatProps> = ({
    token,
    userId,
    displayName,
    threadId,
    endpointUrl,
    onClose
}) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [messageText, setMessageText] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [chatDisabled, setChatDisabled] = useState(false);

    const chatThreadClientRef = useRef<ChatThreadClient>();
    const chatClientRef = useRef<ChatClient>();
    const chatBodyRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        let isMounted = true;

        const setupChat = async () => {
            try {
                const credential = new AzureCommunicationTokenCredential(token);
                const chatClient = new ChatClient(endpointUrl, credential);
                chatClientRef.current = chatClient;

                await chatClient.startRealtimeNotifications();

                const threadClient = await chatClient.getChatThreadClient(threadId);
                chatThreadClientRef.current = threadClient;

                const loadedMessages: ChatMessage[] = [];
                for await (const message of threadClient.listMessages()) {
                    if (message.type === "text" || message.type === "html") {
                        loadedMessages.push(message);
                    }
                }

                if (isMounted) {
                    setMessages(loadedMessages.reverse());
                    setIsLoading(false);
                }

                const handleMessageReceived = (event: ChatMessageReceivedEvent) => {
                    if (
                        event.threadId === threadId &&
                        (event.type === "text" || event.type?.toLowerCase().includes("text"))
                    ) {
                        if (!isMounted) return;

                        setMessages(prev => {
                            if (prev.find(msg => msg.id === event.id)) {
                                return prev;
                            }

                            const newMessage: ChatMessage = {
                                id: event.id,
                                type: "text",
                                sequenceId: "",
                                version: "",
                                content: { message: event.message },
                                senderDisplayName: event.senderDisplayName,
                                createdOn: event.createdOn ?? new Date(),
                                sender: event.sender,
                                metadata: event.metadata
                            };
                            return [...prev, newMessage];
                        });
                    }
                };

                chatClient.on('chatMessageReceived', handleMessageReceived);

                return () => {
                    isMounted = false;
                    chatClient.off('chatMessageReceived', handleMessageReceived);
                };
            } catch (error) {
                console.error('Erro ao inicializar chat:', error);
            }
        };

        const cleanup = setupChat();

        return () => {
            isMounted = false;

            if (cleanup instanceof Promise) {
                cleanup.then(fn => fn?.());
            }
        };
    }, [token, threadId, endpointUrl]);


    const sendMessage = async () => {
        const trimmed = messageText.trim();
        if (!trimmed || !chatThreadClientRef.current || chatDisabled) return;

        try {
            await chatThreadClientRef.current.sendMessage(
                { content: trimmed },
                { senderDisplayName: displayName }
            );

            setMessageText('');
        } catch (err: any) {
            setMessageText('');
            console.error('Erro ao enviar mensagem:', err);

            if (err?.statusCode === 403 || err?.code === "Forbidden") {
                setChatDisabled(true);
            }
        }
    };

    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTo({
                top: chatBodyRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [messages, isLoading]);

    const chatItems: ChatItemProps[] = messages.map((msg, index) => {
        const isMine = (msg.sender as CommunicationUserIdentifier)?.communicationUserId === userId;

        return {
            key: msg.id ?? `msg-${index}`,
            contentPosition: isMine ? 'end' : 'start',
            attached: false,
            message: (
                <Chat.Message
                    className={`${isMine ? 'mine' : 'other'}`}
                    author={msg.senderDisplayName ?? ''}
                    timestamp={msg.createdOn?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    content={{
                        content: (
                            <div dangerouslySetInnerHTML={{ __html: msg.content?.message ?? '' }} />
                        )
                    }}
                    mine={isMine}
                />
            )
        };
    });

    const ChatSkeleton = () => {
        return (
            <div className="chat-skeleton">
                {Array.from({ length: 6 }).map((_, index) => {
                    const isMine = index % 2 === 0;

                    return (
                        <div
                            key={index}
                            className={`ui-chat__item__message ${isMine ? 'mine' : 'other'}`}
                            style={{
                                alignItems: isMine ? 'flex-end' : 'flex-start',
                                display: 'flex',
                                flexDirection: 'column',
                                marginBottom: '1rem',
                                width: '100%',
                            }}
                        >
                            <div className="ui-chat__messageheader" style={{ marginBottom: '.5rem' }}>
                                <Skeleton.Line width="80px" />
                            </div>
                            <div
                                className="ui-chat__messagecontent"
                                style={{
                                    backgroundColor: isMine ? '#e8ebfa' : '#f5f5f5',
                                    borderRadius: '6px',
                                    padding: '.8rem 1rem',
                                    maxWidth: '70%',
                                    width: !isMine ? '20rem' : '5rem',
                                    minWidth: '100px',
                                }}
                            >
                                <Skeleton animation="pulse">
                                    <Skeleton.Line width="100%" />
                                    <Skeleton.Line width="60%" />
                                </Skeleton>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="chat-panel">
            <div className="chat-header">
                <h3>Chat</h3>
                <Button
                    icon={<CloseIcon />}
                    text
                    className="close-panel-btn"
                    onClick={onClose}
                />
            </div>

            <div className="chat-body" ref={chatBodyRef}>
                {isLoading ? <ChatSkeleton /> : <Chat items={chatItems} />}
                <div ref={messagesEndRef} />
            </div>

            <div className="chat-footer">
                <Input
                    fluid
                    placeholder={!chatDisabled ? "Digite uma mensagem..." : "Chat desabilitado"}
                    value={messageText}
                    disabled={chatDisabled}
                    onChange={(e, data) => setMessageText(data?.value ?? '')}
                    onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                />
                <Button disabled={chatDisabled} iconOnly primary icon={<SendIcon />} onClick={sendMessage} />
            </div>
        </div>
    );
};

export default CustomChat;
